import { FormattedMessage } from 'react-intl';
import { StarIcon } from '@synergy/theme';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { padZeroLeft } from '@synergy/utils';

import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { Contact } from '../../../models/contact';
import { ContactDetails } from '../contact-details';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';

export interface PartialContact extends BaseAutocompleteSuggestionType {
    fullName: string;
    positionName: string;
    isLocked: boolean;
}

const styles = (theme: Theme) =>
    createStyles({
        icon: { verticalAlign: 'middle', lineHeight: 1, color: theme.palette.primary.main },
    });

export type PoolContactAutocompleteListProps = WithStyles<typeof styles>;

class PoolContactAutocompleteListBase extends PoolFormAutocompleteList<
    Contact,
    PartialContact,
    PoolContactAutocompleteListProps
> {
    public label = (<FormattedMessage id="app.common.responsibleContact" />);

    public getSuggestionValue = (suggestion: PartialContact) => suggestion && suggestion.fullName;
    public getDetailContent = (item?: PartialContact) =>
        item?.id && <ContactDetails contactId={+item.id} key={+item.id} />;

    /**
     * Who may still be chosen here.
     *
     * A locked person is one nobody works with any more, so they are not
     * offered — neither as a partner nor as the one responsible (LST-S14, GF
     * 12.10.2026). The one already on a document keeps standing there: this
     * is the list of suggestions, not what the field shows.
     */
    public getPartial = (items: Contact[]): PartialContact[] => {
        return items
            .filter(contact => !contact.isLocked)
            .map(contact => ({
                id: padZeroLeft(contact.id, 5),
                fullName: contact.fullName,
                isLocked: contact.isLocked,
                tags: contact.getTags(30),
                positionName: contact.positionName || '',
                mainContact: contact.isMainContact ? (
                    <StarIcon sx={{ verticalAlign: 'middle', lineHeight: 1 }} color="primary" />
                ) : (
                    <div />
                ),
            }));
    };
}
export const PoolContactAutocompleteList = withStyles(styles)(PoolContactAutocompleteListBase);
